import {
  Component,
  computed,
  inject,
  input,
  output,
  Signal,
} from '@angular/core';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { ConfigurationsSelectors } from 'demand-forecasting/store/configurations/configurations.selectors';
import { isForecastSeriesFetchingOrPending } from 'demand-forecasting/store/forecast-series/forecast-series.selectors';
import { ConfigurationRouterHelperService } from 'demand-forecasting/store/router-state/configuration-router-helper.service';

import { selectTargetUnit } from 'demand-forecasting/store/router-state/router.selectors';
import { FormFieldComponent, FormInputSelectComponent } from '@libs/ui';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ErrorMessageComponent } from '@components/base-elements/error/error-message.component';

@Component({
  selector: 'pm-target-unit-picker',
  imports: [
    MatSelectModule,
    MatOptionModule,
    MatFormFieldModule,
    TranslateModule,
    FormFieldComponent,
    FormInputSelectComponent,
    ReactiveFormsModule,
    FormsModule,
    ErrorMessageComponent,
  ],
  template: `
    @if (targetUnits(); as targetUnits) {
      @let value =
        this.targetUnit()
          ? this.targetUnit()
          : this.selectedTargetUnitFromUrl();

      @if (targetUnits.length > 1) {
        <pm-form-field label="RESULT.TARGET_UNIT_TITLE">
          <pm-form-input-select
            [data]="availableTargetUnits()"
            [clearable]="false"
            [disabled]="isLoading()"
            [ngModel]="value"
            (ngModelChange)="onChangeTargetUnit($event ?? '')"
          />
        </pm-form-field>
      } @else if (!!this.showWarningMessageForNoMultipleTargetUnit()) {
        <pm-error-message
          [message]="'UTILITY.ONLY_ONE_TARGET_UNIT_AVAILABLE'"
        />
      }
    }
  `,
})
export class TargetUnitPickerComponent {
  public targetUnit = input<string | void>();
  public skipTargetUnitUrlUpdate = input<boolean>(false);
  public showWarningMessageForNoMultipleTargetUnit = input<boolean>(false);
  public targetUnitChange = output<string>();

  private readonly store = inject(Store);
  private readonly configurationRouterHelperService = inject(
    ConfigurationRouterHelperService,
  );

  protected isLoading = this.store.selectSignal(
    isForecastSeriesFetchingOrPending,
  );

  private readonly currentConfiguration = this.store.selectSignal(
    ConfigurationsSelectors.selectConfigurationByUrlParam,
  );

  private readonly additionalTargetUnitNames = this.store.selectSignal(
    ConfigurationsSelectors.selectAdditionalTargetUnitNames,
  );
  protected selectedTargetUnitFromUrl =
    this.store.selectSignal(selectTargetUnit);

  protected targetUnits = computed(() => {
    const defaultTargetUnit =
      this.currentConfiguration()?.startForecastConfig.target;
    if (defaultTargetUnit) {
      if (this.additionalTargetUnitNames().includes(defaultTargetUnit)) {
        return this.additionalTargetUnitNames();
      }
      return this.additionalTargetUnitNames().concat(defaultTargetUnit);
    }
    return [];
  });

  protected availableTargetUnits: Signal<Record<string, string>[]> = computed(
    () => {
      const targetUnits = this.targetUnits();
      return targetUnits.map((targetUnit) => ({
        value: targetUnit,
        label: targetUnit,
      }));
    },
  );

  onChangeTargetUnit(newValue: (string | undefined)[] | string) {
    let value = '';
    if (Array.isArray(newValue)) {
      value = newValue[0] ?? '';
    } else {
      value = newValue;
    }
    if (!this.skipTargetUnitUrlUpdate()) {
      this.configurationRouterHelperService.updateTargetUnit(value);
    }
    this.targetUnitChange.emit(value);
  }
}
